import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { Col, Row, Card } from "react-bootstrap";
import MaterialReactTable, { type MRT_ColumnDef } from "material-react-table";
import moment from "moment";
import { datetimeFormats, inspectionTypes, machineStatusType } from "../../constants/constants";
import {
  downloadCertificateApiCall,
  downloadDocumentsApiCall,
  inspectionsApiCall,
} from "services/inspections";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Tooltip from "react-bootstrap/Tooltip";
import ImageTag from "components/common/ImageTag";
import DownloadIocon from "assets/images/download-icon.svg";
import { toast } from "react-toastify";
import { nameBasedProtectedRoutes } from "router/protected";
import { Pagination } from "@mui/material";
import ShareModel from "../Inspections/ShareModel";

/**
 * Create a custom component which will render the matched Inspections - Machines/Devices
 * list on the dashboard, only while the master search is active.
 * @param props
 * @returns Inspected Machines component
 */
export const InspectedMachines = (props: { userId: number; search: string }) => {
  const [data, setData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalRow, setTotalRow] = useState(0);
  const [fromRows, setFromRows] = useState(0);
  const [toRows, setToRows] = useState(0);
  const [shareModelVisible, setShareModelVisible] = useState(false);
  const [shareData, setShareData] = useState({});

  useEffect(() => {
    setCurrentPage(1);
  }, [props.search]);

  useEffect(() => {
    if (!props.search) {
      setData([]);
      setTotalPages(0);
      setTotalRow(0);
      return;
    }
    fetchInspectedMachines();
  }, [props.search, currentPage]);

  const fetchInspectedMachines = () => {
    const param = {
      status: "",
      sortColumn: "Inspection_machine.updated_at",
      sortDirection: "desc",
      inspectionDate: "",
      srNumber: "",
      inspector: "",
      machineStatus: "",
      search: props.search,
      page: currentPage,
      limit: 10,
    };
    inspectionsApiCall(param, props.userId).then((response) => {
      setData(response.resultRows);
      setTotalPages(response.totalPages);
      setTotalRow(response.totalResults);
      const fromRowsVal =
        response.resultRows.length > 0
          ? (response.page - 1) * response.limit + 1
          : 0;
      let toRowsVal = fromRowsVal + response.resultRows.length - 1;
      toRowsVal = toRowsVal > 0 ? toRowsVal : 0;
      setFromRows(fromRowsVal);
      setToRows(toRowsVal);
    });
  };

  const downloadDocuments = (id: number) => {
    downloadDocumentsApiCall(id)
      .then((response) => {
        window.open(response.data, "_blank");
      })
      .catch((error) => {
        toast.error(error.response.data.message);
      });
  };

  const downloadCertificate = (id: number) => {
    downloadCertificateApiCall(id)
      .then((response) => {
        window.open(response.data, "_blank");
      })
      .catch((error) => {
        toast.error(error.response.data.message);
      });
  };

  const shareDocumentModel = (row: any) => {
    setShareData(row);
    setShareModelVisible(!shareModelVisible);
  };

  const handleCloseModel = () => {
    setShareData({});
    setShareModelVisible(false);
  };

  const downloadExcelReport = (url: string) => {
    const fileName = url.split("/").pop();
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", fileName as string);
    link.style.display = "none";
    link.target = "_blank";
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  /* Column Definition requires for MaterialReactTable */
  type InspectedMachine = {
    machine_id: number;
    customer_id: number;
    document_transferred: boolean;
    Inspection_machine: {
      id: number;
      user_id: number;
      device_id: string;
      make: string;
      model_number: string;
      serial_number: string;
      inspection_type: string;
      status: string;
      Inspection: {
        id: number;
        sr_number: string;
        inspection_date: string;
      };
      certificate: {
        issue_date: string;
        machine_status: string;
        excel_dataset: string;
      };
    };
  };

  const tableColumns = useMemo<MRT_ColumnDef<InspectedMachine>[]>(
    () => [
      {
        accessorKey: "Inspection_machine.inspection_id",
        header: "SR NO.",
        Cell: ({ row }) =>
          row.original?.customer_id == row.original?.Inspection_machine?.user_id ? (
            <Link
              to={`${nameBasedProtectedRoutes.inspectionDetail.path}/${row.original?.Inspection_machine?.Inspection?.id}`}
              className="text-decoration-none text-link"
            >
              <strong>{row.original?.Inspection_machine?.Inspection?.sr_number}</strong>
            </Link>
          ) : (
            <strong>{row.original?.Inspection_machine?.Inspection?.sr_number}</strong>
          ),
      },
      {
        accessorKey: "Inspection_machine.device_id",
        header: "DEVICE ID",
      },
      {
        accessorKey: "Inspection_machine.make",
        header: "MAKE",
      },
      {
        accessorKey: "Inspection_machine.model_number",
        header: "MODEL NO.",
      },
      {
        accessorKey: "Inspection_machine.serial_number",
        header: "SERIAL NO.",
      },
      {
        accessorKey: "Inspection_machine.Inspection.inspection_date",
        header: "INSPECTION DATE",
        Cell: ({ row }) => {
          return row.original?.Inspection_machine?.Inspection?.inspection_date
            ? moment(row.original?.Inspection_machine?.Inspection?.inspection_date).format(
                datetimeFormats.separatedDate
              )
            : "-";
        },
      },
      {
        accessorKey: "Inspection_machine.inspection_type",
        header: "INSPECTION TYPE",
        Cell: ({ row }) => {
          const type = row.original?.Inspection_machine?.inspection_type;
          const found = inspectionTypes.find((item) => item.key === type);
          return found ? found.value : type;
        },
      },
      {
        accessorKey: "Inspection_machine.certificate.issue_date",
        header: "CERTIFICATE DATE",
        Cell: ({ row }) => {
          return row.original?.Inspection_machine?.certificate?.issue_date
            ? moment(row.original?.Inspection_machine?.certificate?.issue_date).format(
                datetimeFormats.separatedDate
              )
            : "-";
        },
      },
      {
        accessorKey: "Inspection_machine.certificate.machine_status",
        header: "STATUS",
        Cell: ({ row }) => {
          switch (row.original?.Inspection_machine?.certificate?.machine_status) {
            case machineStatusType.expired:
              return (
                <span className="text-danger bg-light-red fs-15 p-1 pe-2 ps-2 text-light-red cap-text border-radius-5">
                  {machineStatusType.expired}
                </span>
              );
            case machineStatusType.active:
              return (
                <span className="text-success bg-ligt-green fs-15 p-1 pe-2 ps-2 text-green cap-text border-radius-5">
                  {machineStatusType.active}
                </span>
              );
            default:
              return row.original?.Inspection_machine?.status?.toUpperCase();
          }
        },
      },
      {
        accessorKey: "machine_id",
        header: "ACTION",
        enableSorting: false,
        Cell: ({ row }) => {
          return (
            <>
              <OverlayTrigger overlay={<Tooltip>Download Document Set</Tooltip>}>
                <span
                  role="button"
                  onClick={() =>
                    row.original?.customer_id == row.original?.Inspection_machine?.user_id ||
                    row.original?.document_transferred
                      ? downloadDocuments(row.original?.machine_id)
                      : downloadCertificate(row.original?.machine_id)
                  }
                  className="d-inline-block"
                >
                  <ImageTag src={DownloadIocon} alt="Download Document Set" />
                </span>
              </OverlayTrigger>{" "}
              <i
                onClick={() => {
                  shareDocumentModel(row.original);
                }}
                className="pointer-cursor fas fa fa-share-alt mx-2 text-link"
              />
              {row.original?.Inspection_machine?.certificate?.excel_dataset && (
                <OverlayTrigger overlay={<Tooltip>Download DataSet</Tooltip>}>
                  <i
                    className="pointer-cursor fas fa fa-file-excel mx-2"
                    onClick={() => {
                      downloadExcelReport(
                        row.original?.Inspection_machine?.certificate?.excel_dataset
                      );
                    }}
                  />
                </OverlayTrigger>
              )}
            </>
          );
        },
      },
    ],
    []
  );

  if (!props.search || totalRow === 0) {
    return null;
  }

  return (
    <Row className="mb-4">
      <Col md={12}>
        <Card className="border-0">
          <Card.Header className="border-0 bg-default bg-theme black-color fs-20 fw-bold ls-2 btlr-14 btrr-14 pe-4 ps-4 pt-11">
            INSPECTIONS - MACHINES / DEVICES
          </Card.Header>
          <Card.Body className="pe-4 ps-4 pt-4">
            <div className="table-responsive dashboard-table-data">
              <MaterialReactTable
                muiTableProps={{
                  classes: {
                    root: "table custom-table border-none-table table-striped machine-custome-table",
                  },
                }}
                enableGlobalFilter={false}
                enableFullScreenToggle={false}
                enableDensityToggle={false}
                enableHiding={false}
                enableColumnFilters={false}
                enableSorting={false}
                enablePagination={false}
                columns={tableColumns}
                data={data}
              />
              {totalPages > 0 ? (
                <div className="paggination">
                  <p className="showing-text">
                    Showing {fromRows}-{toRows} of {totalRow}
                  </p>
                  <Pagination
                    count={totalPages}
                    page={currentPage}
                    shape="rounded"
                    variant="outlined"
                    onChange={(event, page) => setCurrentPage(page)}
                  />
                </div>
              ) : (
                ""
              )}
            </div>
          </Card.Body>
        </Card>
      </Col>
      {shareModelVisible && (
        <ShareModel
          handleCloseModel={handleCloseModel}
          shareModelVisible={shareModelVisible}
          data={shareData}
        />
      )}
    </Row>
  );
};
